import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getOrders, getAddresses, deleteAddress, updateAddress, updateUser,
  getMySecondHandItems, deleteSecondHandItem, changePassword, initiatePayment,
  getPaymentMethods,
} from '../api';
import SecondHandCard from '../components/common/SecondHandCard';
import {
  FiPackage, FiMapPin, FiUser, FiEdit2, FiTrash2, FiStar, FiTag, FiPlus,
  FiLock, FiEye, FiEyeOff, FiShoppingBag, FiTruck, FiCreditCard, FiArrowRight,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Dashboard.css';

const NPR = (n) => 'NPR ' + parseFloat(n || 0).toLocaleString('en-NP');

const ORDER_STATUS_BADGE = {
  pending: 'badge-yellow', processing: 'badge-blue', paid: 'badge-blue',
  shipped: 'badge-blue', delivered: 'badge-green', cancelled: 'badge-red',
};

const formatDate = (value) =>
  new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const Dashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'orders');
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [listings, setListings] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingAddrs, setLoadingAddrs] = useState(false);
  const [loadingListings, setLoadingListings] = useState(false);
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [payingOrder, setPayingOrder] = useState(null);
  const [gateways, setGateways] = useState([]);

  useEffect(() => {
    if (user) setProfileForm({
      first_name: user.first_name || '', last_name: user.last_name || '',
      email: user.email || '', phone: user.phone || '',
    });
  }, [user]);

  // Orders and listings feed the stat row, so both load up front rather than
  // only when their tab is opened.
  useEffect(() => {
    setLoadingOrders(true);
    getOrders()
      .then(({ data }) => setOrders(data.results || []))
      .catch(() => toast.error('Could not load your orders'))
      .finally(() => setLoadingOrders(false));
    getMySecondHandItems()
      .then(({ data }) => setListings(data.results || data || []))
      .catch(() => {});
    getPaymentMethods()
      .then(({ data }) => setGateways(data.online || []))
      .catch(() => setGateways([]));
  }, []);

  useEffect(() => {
    if (tab === 'addresses') {
      setLoadingAddrs(true);
      getAddresses().then(({ data }) => setAddresses(data.results || [])).finally(() => setLoadingAddrs(false));
    }
    if (tab === 'listings' && listings.length === 0) {
      setLoadingListings(true);
      getMySecondHandItems()
        .then(({ data }) => setListings(data.results || data || []))
        .catch(() => toast.error('Could not load your listings'))
        .finally(() => setLoadingListings(false));
    }
  }, [tab]);

  // Deep-linkable tabs, e.g. /dashboard?tab=listings
  useEffect(() => {
    const urlTab = searchParams.get('tab') || 'orders';
    setTab((prev) => (urlTab === prev ? prev : urlTab));
  }, [searchParams]);

  const selectTab = (key) => {
    setTab(key);
    const p = new URLSearchParams(searchParams);
    if (key === 'orders') p.delete('tab'); else p.set('tab', key);
    setSearchParams(p, { replace: true });
  };

  const stats = useMemo(() => {
    const active = orders.filter((o) => !['delivered', 'cancelled'].includes(o.status));
    const spent = orders
      .filter((o) => o.status !== 'cancelled')
      .reduce((sum, o) => sum + parseFloat(o.total || 0), 0);
    return { total: orders.length, active: active.length, spent };
  }, [orders]);

  const handleDeleteListing = async (slug) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await deleteSecondHandItem(slug);
      setListings((prev) => prev.filter((l) => l.slug !== slug));
      toast.success('Listing deleted');
    } catch { toast.error('Could not delete listing'); }
  };

  // Resume an online payment for an order that was left unpaid
  const handlePayNow = async (order, provider) => {
    setPayingOrder(order.id);
    try {
      const { data } = await initiatePayment({ order: order.id, provider });
      sessionStorage.setItem('pending_payment', JSON.stringify({
        provider, payment_id: data.payment_id, order,
      }));
      window.location.href = data.payment_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start the payment');
      setPayingOrder(null);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try { await updateUser(user.id, profileForm); toast.success('Profile updated'); }
    catch { toast.error('Update failed'); }
    finally { setSavingProfile(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.old_password || !pwForm.new_password) {
      toast.error('Fill in your current and new password');
      return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('The new passwords do not match');
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setSavingPw(true);
    try {
      const { data } = await changePassword(pwForm);
      // The API returns a fresh JWT pair so the session survives the change
      if (data.access) localStorage.setItem('access_token', data.access);
      if (data.refresh) localStorage.setItem('refresh_token', data.refresh);
      setPwForm({ old_password: '', new_password: '', confirm_password: '' });
      toast.success('Password updated');
    } catch (err) {
      const d = err.response?.data || {};
      const msg = d.old_password?.[0] || d.new_password?.[0] || d.confirm_password?.[0]
        || d.detail || d.non_field_errors?.[0] || 'Could not update password';
      toast.error(msg);
    } finally {
      setSavingPw(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Remove this address?')) return;
    try {
      await deleteAddress(id);
      setAddresses((prev) => prev.filter((a) => a.id !== id));
      toast.success('Address removed');
    } catch { toast.error('Failed to remove'); }
  };

  const handleSetDefault = async (id) => {
    try {
      await updateAddress(id, { is_default: true });
      setAddresses((prev) => prev.map((a) => ({ ...a, is_default: a.id === id })));
      toast.success('Default address updated');
    } catch { toast.error('Failed to update'); }
  };

  const TABS = [
    { key: 'orders', label: 'My Orders', icon: <FiPackage />, count: orders.length || null },
    { key: 'listings', label: 'My Listings', icon: <FiTag />, count: listings.length || null },
    { key: 'addresses', label: 'Addresses', icon: <FiMapPin />, count: addresses.length || null },
    { key: 'profile', label: 'Profile', icon: <FiUser /> },
  ];

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : user?.username;

  return (
    <div className="dash">
      <div className="container">
        {/* ---- Header ------------------------------------------------- */}
        <header className="dash-head">
          <div>
            <span className="eyebrow">My Account</span>
            <h1 className="page-title">Welcome back, {user?.first_name || user?.username}</h1>
            <p className="dash-head-sub">
              Track orders, manage your pre-owned listings and keep your details current.
            </p>
          </div>
          <Link to="/products" className="btn btn-outline">
            Continue Shopping <FiArrowRight />
          </Link>
        </header>

        {/* ---- Stat row ----------------------------------------------- */}
        <div className="dash-stats">
          <div className="dash-stat">
            <span className="dash-stat-icon"><FiShoppingBag /></span>
            <div>
              <strong>{stats.total}</strong>
              <span>Orders placed</span>
            </div>
          </div>
          <div className="dash-stat">
            <span className="dash-stat-icon"><FiTruck /></span>
            <div>
              <strong>{stats.active}</strong>
              <span>In progress</span>
            </div>
          </div>
          <div className="dash-stat">
            <span className="dash-stat-icon"><FiTag /></span>
            <div>
              <strong>{listings.length}</strong>
              <span>Listings</span>
            </div>
          </div>
          <div className="dash-stat">
            <span className="dash-stat-icon"><FiCreditCard /></span>
            <div>
              <strong>{NPR(stats.spent)}</strong>
              <span>Lifetime spend</span>
            </div>
          </div>
        </div>

        <div className="dash-layout">
          {/* ---- Sidebar ---------------------------------------------- */}
          <aside className="dash-sidebar">
            <div className="dash-user card">
              <div className="dash-avatar">{(user?.first_name || user?.username || 'U')[0].toUpperCase()}</div>
              <strong>{displayName}</strong>
              <small className="text-muted">{user?.email}</small>
            </div>

            <nav className="dash-nav">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  className={`dash-nav-item ${tab === t.key ? 'active' : ''}`}
                  onClick={() => selectTab(t.key)}
                >
                  {t.icon}
                  <span>{t.label}</span>
                  {t.count ? <em className="dash-nav-count">{t.count}</em> : null}
                </button>
              ))}
            </nav>

            <Link to="/second-hand/sell" className="dash-sell-cta">
              <FiPlus />
              <div>
                <strong>Sell your DJI gear</strong>
                <span>List it in the pre-owned marketplace</span>
              </div>
            </Link>
          </aside>

          {/* ---- Content ---------------------------------------------- */}
          <div className="dash-content">

            {/* ORDERS */}
            {tab === 'orders' && (
              <section className="dash-panel">
                <div className="dash-panel-head">
                  <h2>My Orders</h2>
                  <p className="text-muted">Everything you've ordered, newest first.</p>
                </div>

                {loadingOrders ? (
                  <div className="dash-skeletons">
                    {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 132 }} />)}
                  </div>
                ) : orders.length === 0 ? (
                  <div className="dash-empty">
                    <span className="dash-empty-icon">📦</span>
                    <h3>No orders yet</h3>
                    <p className="text-muted">Your orders will appear here once you check out.</p>
                    <Link to="/products" className="btn btn-primary">Browse Products</Link>
                  </div>
                ) : (
                  <div className="order-list">
                    {orders.map((o) => (
                      <article key={o.id} className="order-card">
                        <div className="order-card-top">
                          <div className="order-ids">
                            <span className="order-num">Order #{o.id}</span>
                            {o.tracking_id && <code className="order-track">{o.tracking_id}</code>}
                          </div>
                          <span className={`badge ${ORDER_STATUS_BADGE[o.status] || 'badge-gray'}`}>
                            {o.status}
                          </span>
                        </div>

                        <div className="order-card-items">
                          {o.items?.length > 0 ? o.items.map((item) => (
                            <span key={item.id} className="order-chip">
                              <em>×{item.quantity}</em>
                              {(item.second_hand_item || item.product)?.title || 'Product'}
                              {item.second_hand_item && <b className="order-chip-used">pre-owned</b>}
                            </span>
                          )) : <span className="text-muted">No item details</span>}
                        </div>

                        <div className="order-card-foot">
                          <div className="order-meta">
                            <span>{formatDate(o.created_at)}</span>
                            {o.phone && <span>· {o.phone}</span>}
                          </div>
                          <div className="order-total-wrap">
                            {o.status === 'pending' && gateways.length > 0 && (
                              <div className="order-pay">
                                {gateways.map((g, i) => (
                                  <button
                                    key={g.provider}
                                    className={`btn btn-sm ${i === 0 ? 'btn-primary' : 'btn-outline'}`}
                                    onClick={() => handlePayNow(o, g.provider)}
                                    disabled={payingOrder === o.id}
                                  >
                                    {payingOrder === o.id && i === 0
                                      ? 'Starting…'
                                      : i === 0 ? `Pay with ${g.label}` : g.label}
                                  </button>
                                ))}
                              </div>
                            )}
                            <span className="order-total">{NPR(o.total)}</span>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* SECOND-HAND LISTINGS */}
            {tab === 'listings' && (
              <section className="dash-panel">
                <div className="dash-panel-head with-action">
                  <div>
                    <h2>My Second-Hand Listings</h2>
                    <p className="text-muted">
                      Listings stay private until an admin approves them — you can always see yours here.
                    </p>
                  </div>
                  <Link to="/second-hand/sell" className="btn btn-primary btn-sm">
                    <FiPlus /> New Listing
                  </Link>
                </div>

                {loadingListings ? (
                  <div className="grid-3">
                    {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 300 }} />)}
                  </div>
                ) : listings.length === 0 ? (
                  <div className="dash-empty">
                    <span className="dash-empty-icon">♻️</span>
                    <h3>Nothing listed yet</h3>
                    <p className="text-muted">Sell your used DJI gear to other pilots in Nepal.</p>
                    <Link to="/second-hand/sell" className="btn btn-primary"><FiPlus /> List an Item</Link>
                  </div>
                ) : (
                  <div className="dash-listings">
                    {listings.map((item) => (
                      <div key={item.id} className="dash-listing">
                        <SecondHandCard item={item} showStatus />
                        <div className="dash-listing-actions">
                          {!item.is_sold && (
                            <Link to={`/second-hand/${item.slug}/edit`} className="btn btn-outline btn-sm">
                              <FiEdit2 /> Edit
                            </Link>
                          )}
                          <button
                            className="btn btn-ghost btn-sm dash-listing-delete"
                            onClick={() => handleDeleteListing(item.slug)}
                          >
                            <FiTrash2 /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ADDRESSES */}
            {tab === 'addresses' && (
              <section className="dash-panel">
                <div className="dash-panel-head">
                  <h2>Saved Addresses</h2>
                  <p className="text-muted">Addresses are saved automatically when you check out.</p>
                </div>

                {loadingAddrs ? (
                  <div className="grid-2">
                    {[...Array(2)].map((_, i) => <div key={i} className="skeleton" style={{ height: 160 }} />)}
                  </div>
                ) : addresses.length === 0 ? (
                  <div className="dash-empty">
                    <span className="dash-empty-icon">📍</span>
                    <h3>No saved addresses</h3>
                    <p className="text-muted">The address you use at checkout will be stored here.</p>
                  </div>
                ) : (
                  <div className="addr-grid">
                    {addresses.map((a) => (
                      <div key={a.id} className={`addr-card ${a.is_default ? 'default' : ''}`}>
                        <div className="addr-card-header">
                          {a.is_default
                            ? <span className="badge badge-red"><FiStar /> Default</span>
                            : <span className="badge badge-gray">Address</span>}
                          <div className="addr-card-actions">
                            {!a.is_default && (
                              <button className="addr-action-btn" title="Set as default" onClick={() => handleSetDefault(a.id)}>
                                <FiStar />
                              </button>
                            )}
                            <button className="addr-action-btn delete" title="Remove" onClick={() => handleDeleteAddress(a.id)}>
                              <FiTrash2 />
                            </button>
                          </div>
                        </div>
                        <div className="addr-card-body">
                          <p className="addr-line1">{a.line1}</p>
                          {a.line2 && <p>{a.line2}</p>}
                          <p>{a.city}, {a.state}</p>
                          {a.postal_code && <p>{a.postal_code}, {a.country}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* PROFILE */}
            {tab === 'profile' && (
              <>
                <section className="dash-panel">
                  <div className="dash-panel-head">
                    <h2>Profile</h2>
                    <p className="text-muted">How we address you and where we reach you.</p>
                  </div>

                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="form-label">First Name</label>
                      <input value={profileForm.first_name} onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <input value={profileForm.last_name} onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input type="email" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} />
                    </div>
                  </div>

                  <div className="dash-panel-actions">
                    <button className="btn btn-primary" onClick={saveProfile} disabled={savingProfile}>
                      <FiEdit2 /> {savingProfile ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </section>

                <section className="dash-panel">
                  <div className="dash-panel-head with-action">
                    <div>
                      <h2><FiLock /> Password</h2>
                      <p className="text-muted">You stay signed in on this device after changing it.</p>
                    </div>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowPw((v) => !v)}>
                      {showPw ? <FiEyeOff /> : <FiEye />} {showPw ? 'Hide' : 'Show'}
                    </button>
                  </div>

                  <form onSubmit={handleChangePassword}>
                    <div className="form-grid-2">
                      <div className="form-group span-2">
                        <label className="form-label">Current Password</label>
                        <input
                          type={showPw ? 'text' : 'password'}
                          autoComplete="current-password"
                          value={pwForm.old_password}
                          onChange={(e) => setPwForm({ ...pwForm, old_password: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">New Password</label>
                        <input
                          type={showPw ? 'text' : 'password'}
                          autoComplete="new-password"
                          value={pwForm.new_password}
                          onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                        />
                        <small className="field-hint">At least 6 characters.</small>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Confirm New Password</label>
                        <input
                          type={showPw ? 'text' : 'password'}
                          autoComplete="new-password"
                          value={pwForm.confirm_password}
                          onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="dash-panel-actions">
                      <button type="submit" className="btn btn-primary" disabled={savingPw}>
                        <FiLock /> {savingPw ? 'Updating…' : 'Update Password'}
                      </button>
                    </div>
                  </form>
                </section>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
